import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { toast } from 'react-toastify';
import customizationApi from '../../utils/customizationApi';

const CARDS = [
  { value: 'one_to_one', label: '1 : 1' },
  { value: 'one_to_many', label: '1 : M' },
  { value: 'many_to_one', label: 'M : 1' },
  { value: 'many_to_many', label: 'M : M' }
];

const ModuleRelationshipsAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [gate, setGate] = useState(null);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    source_module_code: 'contacts',
    target_module_code: 'accounts',
    cardinality: 'one_to_many',
    tab_label: '',
    display_order: 0,
    is_active: true
  });

  const allowedCodes = gate?.knownModules?.filter((m) => gate.modules?.[m.code]).map((m) => m.code) || [];

  const load = async () => {
    setLoading(true);
    try {
      const [g, r] = await Promise.all([
        customizationApi.get('/v1/admin/ui-gate'),
        customizationApi.get('/v1/admin/relationships')
      ]);
      setGate(g.data?.data || null);
      setRows(r.data?.data?.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const saveNew = async () => {
    try {
      await customizationApi.post('/v1/admin/relationships', form);
      toast.success('Relationship created');
      setOpen(false);
      setForm({
        source_module_code: 'contacts',
        target_module_code: 'accounts',
        cardinality: 'one_to_many',
        tab_label: '',
        display_order: 0,
        is_active: true
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Create failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this relationship?')) return;
    try {
      await customizationApi.delete(`/v1/admin/relationships/${id}`);
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Delete failed');
    }
  };

  const labelFor = (code) => gate?.knownModules?.find((m) => m.code === code)?.label || code;

  if (!gate?.showCustomizationMenus) {
    return (
      <Box p={2}>
        <Alert severity="info">
          Enable customization for the application and modules under <strong>Customize application settings</strong>{' '}
          first.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={2}>
      <Typography variant="h5" gutterBottom>
        Module relationships
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Declare how modules link for future related tabs (e.g. Contact → Accounts). Cardinality describes the logical
        shape for UI and future join rules.
      </Typography>

      <Button startIcon={<AddIcon />} variant="contained" sx={{ mb: 2 }} onClick={() => setOpen(true)}>
        Add relationship
      </Button>

      {loading ? (
        <CircularProgress />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Source</TableCell>
              <TableCell>Target</TableCell>
              <TableCell>Cardinality</TableCell>
              <TableCell>Tab label</TableCell>
              <TableCell>Active</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id}>
                <TableCell>{labelFor(r.source_module_code)}</TableCell>
                <TableCell>{labelFor(r.target_module_code)}</TableCell>
                <TableCell>{CARDS.find((c) => c.value === r.cardinality)?.label || r.cardinality}</TableCell>
                <TableCell>{r.tab_label}</TableCell>
                <TableCell>{r.is_active ? 'Yes' : 'No'}</TableCell>
                <TableCell>
                  <Button size="small" color="error" onClick={() => remove(r.id)}>
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New relationship</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            select
            label="Source module"
            value={form.source_module_code}
            onChange={(e) => setForm((f) => ({ ...f, source_module_code: e.target.value }))}
          >
            {allowedCodes.map((c) => (
              <MenuItem key={c} value={c}>
                {labelFor(c)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Target module"
            value={form.target_module_code}
            onChange={(e) => setForm((f) => ({ ...f, target_module_code: e.target.value }))}
          >
            {allowedCodes.map((c) => (
              <MenuItem key={c} value={c}>
                {labelFor(c)}
              </MenuItem>
            ))}
          </TextField>
          <TextField select label="Cardinality" value={form.cardinality} onChange={(e) => setForm((f) => ({ ...f, cardinality: e.target.value }))}>
            {CARDS.map((c) => (
              <MenuItem key={c.value} value={c.value}>
                {c.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField label="Tab label" value={form.tab_label} onChange={(e) => setForm((f) => ({ ...f, tab_label: e.target.value }))} />
          <TextField
            type="number"
            label="Display order"
            value={form.display_order}
            onChange={(e) => setForm((f) => ({ ...f, display_order: parseInt(e.target.value, 10) || 0 }))}
          />
          <FormControlLabel
            control={<Switch checked={form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} />}
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveNew}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ModuleRelationshipsAdmin;
